import {
  loadTables,
  memoized,
  publishRoutineFailure,
  queryClients,
  queryMetadata,
} from '@/modules/daiwa-house-modular-europe/ProjectIntake/utils';
import { Routines } from '@/modules/daiwa-house-modular-europe/ProjectIntake/Routines';
import moment from 'moment';
import { Reducers } from '@/modules/daiwa-house-modular-europe/ProjectIntake/Reducers';
import {
  buildingEntryNames,
  clientColumns,
  clientEntryNames,
  infoEntryNames,
  metadataTableName,
  risksColumns,
} from '@/modules/daiwa-house-modular-europe/ProjectIntake/TableDefinitions';
import { createUUID } from '@/services/uuid-helper';
import {
  defaultModules,
  rolesToModulesMapping,
} from '@/modules/daiwa-house-modular-europe/ProjectIntake/conceptToProject';
import { value } from 'lodash/seq';
import {
  getProjectMembers,
  provisionProjectV1,
} from '@/services/api/projects.api';
import {
  addTeamUser,
  deleteTeamUser,
  getTeams,
} from '@/services/api/teams.api';
import {
  createRecord,
  deleteRecord,
  updateRecord,
} from '@/services/api/record.api';
import {
  createProjectOS,
  fetchProjectRolesOS,
  fetchProjectSetupOS,
  retryProjectSetupOS,
  fetchProjectV2,
  queryTablesV2,
} from '@/services/api/v2/connect_v2';

// Creating a concept is several calls in a row. Each is a step the dialog shows,
// so a failure says where it stopped and a retry resumes there, on the same
// project, instead of creating a second one.
export const projectSetupSteps = [
  { key: 'project', label: 'Project aanmaken' },
  { key: 'template', label: 'Projecttemplate toepassen' },
  { key: 'v1', label: 'Modules en rollen klaarzetten' },
  { key: 'record', label: 'Concept registreren' },
  { key: 'roles', label: 'Rollen controleren' },
];

const TEMPLATE_POLL_MS = 2000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const state = {
  isValidated: false,
  loadingTables: Routines.newIdle(),
  loadingProjectsMetadata: Routines.newIdle(),
  // null, or { name, date, project, record, missingOsRoles,
  // steps: { [key]: { status, detail } } } with status pending | running | done | failed
  projectSetup: null,
  selectingProject: Routines.newIdle(),
  togglingConcept: Routines.newIdle(),
  // info
  updatingInfo: Routines.newIdle(),
  // client
  loadingClients: Routines.newIdle(),
  creatingClient: Routines.newIdle(),
  removingClient: Routines.newIdle(),
  // layout — @deprecated, see DhmeProjectIntakeLayout.vue
  loadingBuildings: Routines.newIdle(),
  addingBuilding: Routines.newIdle(),
  removingBuilding: Routines.newIdle(),
  selectedBuildingId: null,
  updatingBuildingInfo: Routines.newIdle(),
  addingLayer: Routines.newIdle(),
  removingLayer: Routines.newIdle(),
  // risks
  loadingRisks: Routines.newIdle(),
  storingRisk: Routines.newIdle(),
  removingRisk: Routines.newIdle(),
  // roles
  loadingUsersAndRoles: Routines.newIdle(),

  firm: null,
};

const actions = {
  loadProjectsMetaData(context) {
    return Routines.commit('loadingProjectsMetadata', context, async () => {
      const { projects } = await queryTablesV2({
        tables: [
          {
            name: metadataTableName,
            project: context.rootGetters.project.id,
            as: 'projects',
            columns: [
              {
                name: 'project_id',
              },
              {
                name: 'project_number',
              },
              {
                name: 'name',
              },
              {
                name: 'is_concept',
              },
              {
                name: 'created',
              },
            ],
          },
        ],
      });
      ``;
      return projects;
    }).then(publishRoutineFailure(this));
  },
  loadTablesIfNotExists(context) {
    return Routines.commit(
      'loadingTables',
      context,
      () => loadTables(context.rootGetters.project.id),
      (routine) => Routines.isIdle(routine) || Routines.isFailure(routine) // So only run if it is not already running or already successful
    ).then(publishRoutineFailure(this));
  },
  startProjectSetup(context, name) {
    context.commit('setProjectSetup', {
      name,
      date: moment().format('YYYY-MM-DD'),
      project: null,
      record: null,
      steps: Object.fromEntries(
        projectSetupSteps.map(({ key }) => [
          key,
          { status: 'pending', detail: null },
        ])
      ),
    });

    return context.dispatch('continueProjectSetup');
  },
  // Runs every step not done yet, in order, and stops at the first one that
  // fails. Returns the setup; the dialog reads the outcome from its steps.
  async continueProjectSetup(context) {
    for (const { key } of projectSetupSteps) {
      if (context.state.projectSetup.steps[key].status === 'done') {
        continue;
      }

      const previous = context.state.projectSetup.steps[key].status;
      const progress = (detail) =>
        context.commit('setProjectSetupStep', {
          key,
          status: 'running',
          detail,
        });
      progress(null);

      try {
        const { status = 'done', detail = null } =
          (await projectSetupRunners[key](context, progress, previous)) ?? {};
        context.commit('setProjectSetupStep', { key, status, detail });

        if (status !== 'done') {
          break;
        }
      } catch (error) {
        console.error(error);
        context.commit('setProjectSetupStep', {
          key,
          status: 'failed',
          detail: error?.message ?? String(error),
        });
        break;
      }
    }

    return context.state.projectSetup;
  },
  selectProject(context, projectId) {
    const id = projectId;

    const masterProjectId = context.rootGetters.project.id;

    return Routines.commit('selectingProject', context, async () => {
      const records = await Promise.allSettled([
        queryMetadata(
          masterProjectId,
          id,
          context.getters.tables.metadata.columns
        ),
        fetchProjectV2(id),
      ]);

      return {
        metadata: records[0].value.records[0],
        project: records[1].value,
      };
    }).then(publishRoutineFailure(this));
  },
  async toggleConcept(context) {
    const metadata = context.getters.selectedProjectMetadata;
    const tableId = context.getters.tables.metadata.id;

    const value = !metadata.is_concept;
    const routine = await Routines.commit('togglingConcept', context, () =>
      updateRecord(metadata.id, {
        project: { id: context.rootGetters.project.id },
        table: { id: tableId },
        record: { is_concept: value },
      })
    ).then(publishRoutineFailure(this));

    if (Routines.isSuccess(routine)) {
      context.commit('setIsConcept', value);
    }
  },
  loadClients(context, concept) {
    concept = concept ?? context.getters.selectedProjectMetadata.id;
    const project = context.rootGetters.project.id;

    return Routines.commit('loadingClients', context, () =>
      queryClients(project, concept)
    ).then(publishRoutineFailure(this));
  },
  async addClient(context, client) {
    const routine = await Routines.commit(
      'creatingClient',
      context,
      async () => {
        const projectId = context.rootGetters.project.id;
        const table = context.getters.tables.clients.id;
        const record = {
          project_id: context.rootGetters.project.id,
          ...client,
        };
        return await createRecord({
          project: { id: projectId },
          table: { id: table },
          record,
        });
      },
      (routine) =>
        !Routines.isRunning(routine) ||
        Routines.isSuccess(context.state.loadingClients)
    ).then(publishRoutineFailure(this));

    if (Routines.isSuccess(routine)) {
      context.state.loadingClients.result?.records.push(routine.result);
    }
  },
  async removeClient(context, client) {
    const routine = await Routines.commit(
      'removingClient',
      context,
      () =>
        deleteRecord(client.id, {
          project: { id: context.rootGetters.project.id },
          table: { id: context.getters.tables.clients },
        }),
      (routine) =>
        !Routines.isRunning(routine) ||
        Routines.isSuccess(context.state.loadingClients)
    ).then(publishRoutineFailure(this));

    if (Routines.isSuccess(routine)) {
      context.state.loadingClients.result?.records.reduce(
        ...Reducers.filterInPlace(({ id }) => routine.result.ids.includes(id))
      );
    }
  },
  loadRisks(context) {
    return Routines.commit('loadingRisks', context, async () => {
      const columns = risksColumns.map((name) =>
        name === 'project_id'
          ? {
              name,
              conditions: [
                { operator: '=', value: context.getters.selectedProject.id },
              ],
            }
          : { name }
      );
      const name = context.getters.tables.risks.name;
      const project = context.rootGetters.project.id;
      const { risks } = await queryTablesV2({
        tables: [{ name, project, as: 'risks', columns }],
      });

      risks.records.sort((a, b) => a.ordinal - b.ordinal);

      return risks;
    }).then(publishRoutineFailure(this));
  },
  async storeRisk(context, { title, description, chance, consequence, id }) {
    let tableId = context.getters.tables.risks.id;
    if (id) {
      const risk = { title, description, chance, consequence };
      const routine = await Routines.commit('storingRisk', context, () =>
        updateRecord(id, {
          project: { id: context.rootGetters.project.id },
          table: { id: tableId },
          record: risk,
        })
      ).then(publishRoutineFailure(this));

      if (Routines.isSuccess(routine)) {
        context.commit('updateRisk', risk);
      }
    } else {
      const record = {
        project_id: context.getters.selectedProject.id,
        title,
        description,
        chance,
        consequence,
        ordinal:
          context.getters.risks
            .map((r) => r.ordinal)
            .reduce(...Reducers.max()) + 1,
      };
      const routine = await Routines.commit('storingRisk', context, () =>
        createRecord({
          project: { id: context.rootGetters.project.id },
          table: { id: tableId },
          record,
        })
      ).then(publishRoutineFailure(this));

      if (Routines.isSuccess(routine)) {
        context.commit('addRisk', routine.result);
      }
    }
  },
  async removeRisk(context, risk) {
    const routine = await Routines.commit('removingRisk', context, () =>
      deleteRecord(risk.id, {
        project: { id: context.rootGetters.project.id },
        table: { id: context.getters.tables.risks.id },
      })
    ).then(publishRoutineFailure(this));

    if (Routines.isSuccess(routine)) {
      context.commit('removeRisk', routine.result.ids);
    }
  },
};

const mutations = {
  reset(state) {
    state.selectingProject.result = {};
  },
  setProjectSetup(state, setup) {
    state.projectSetup = setup;
  },
  updateProjectSetup(state, changes) {
    state.projectSetup = { ...state.projectSetup, ...changes };
  },
  setProjectSetupStep(state, { key, status, detail }) {
    state.projectSetup = {
      ...state.projectSetup,
      steps: { ...state.projectSetup.steps, [key]: { status, detail } },
    };
  },
  ...Routines.newMutation('loadingTables'),
  ...Routines.newMutation('loadingProjectsMetadata'),
  ...Routines.newMutation('selectingProject'),
  ...Routines.newMutation('togglingConcept'),
  ...Routines.newMutation('updatingInfo'),
  setIsConcept: (state, value) => {
    const concept = state.selectingProject.result?.metadata;

    if (concept) {
      concept.is_concept = value;
    }
  },
  updateMetadata: (state, { key, value }) => {
    if (state.selectingProject.result?.metadata) {
      state.selectingProject.result.metadata[key] = value;
    }
  },
  ...Routines.newMutation('loadingClients'),
  ...Routines.newMutation('creatingClient'),
  ...Routines.newMutation('removingClient'),
  // layout — @deprecated, see DhmeProjectIntakeLayout.vue
  ...Routines.newMutation('loadingBuildings'),
  ...Routines.newMutation('removingBuilding'),
  ...Routines.newMutation('addingBuilding'),
  ...Routines.newMutation('updatingBuildingInfo'),
  setBuildingInfo(state, { buildingId, name, value }) {
    state.loadingBuildings.result.buildings.records.forEach(
      (b) => b.id === buildingId && (b[name] = value)
    );
  },
  ...Routines.newMutation('addingLayer'),
  ...Routines.newMutation('removingLayer'),
  appendLayer(state, { buildingId, layer }) {
    const buildings = state.loadingBuildings.result.buildings;

    buildings.records = buildings.records.map((building) => {
      if (building.id === buildingId) {
        !building.layers && (building.layers = []);
        building.layers.push(layer);
      }

      return building;
    });
  },
  spliceLayer(state, { buildingId, layers }) {
    const buildings = state.loadingBuildings.result.buildings;

    buildings.records = buildings.records.map((building) => {
      if (building.id === buildingId) {
        building.layers = layers;
      }

      return building;
    });
  },
  ...Routines.newMutation('loadingRisks'),
  ...Routines.newMutation('storingRisk'),
  ...Routines.newMutation('removingRisk'),
  updateRisk(state, risk) {
    state.loadingRisks?.result?.records.forEach((r) => {
      if (r.id === risk.id) {
        Object.assign(r, risk);
      }
    });
  },
  addRisk(state, risk) {
    state.loadingRisks?.result?.records.push(risk);
  },
  removeRisk(state, riskIds) {
    state.loadingRisks?.result?.records?.reduce(
      ...Reducers.filterInPlace((r) => riskIds.includes(r.id))
    );
  },
  ...Routines.newMutation('loadingUsersAndRoles'),
};

const getters = {
  isLoading: (state) => Object.values(state).some(Routines.isRunning),
  tables: (state) => state.loadingTables?.result,
  infoEntries: memoized((state) => (old) => {
    const metadata = state.selectingProject?.result?.metadata ?? {};
    const columns = state.loadingTables.result?.metadata.columns ?? [];

    const valueOf = (column) =>
      Object.entries(metadata).find(([name]) => name === column.name)?.[1] ??
      column.default_value;
    const result = columns
      ?.filter((c) => infoEntryNames.includes(c.name))
      .sort((a, b) => a.order - b.order)
      .map((column) => Object.assign(column, { value: valueOf(column) }));

    return result ?? old;
  }),
  clientEntries: memoized((state) => (old) => {
    const metadata = state.selectingProject?.result?.metadata ?? {};
    const columns = state.loadingTables.result?.metadata.columns ?? [];
    const valueOf = (column) =>
      Object.entries(metadata).find(([name]) => name === column.name)?.[1] ??
      column.default_value;
    const result = columns
      ?.filter((c) => clientEntryNames.includes(c.name))
      .sort((a, b) => a.order - b.order)
      .map((column) =>
        Object.assign(column, {
          value: valueOf(column),
          prefixIcon: clientColumns.find((x) => x.name === column.name)
            ?.prefixIcon,
        })
      );

    return result ?? old;
  }),
  /** @deprecated layout step is no longer used */
  buildingEntries: memoized((state, getters) => (old) => {
    const building =
      state?.loadingBuildings?.result?.buildings?.records?.find(
        (b) => b.id === state.selectedBuildingId
      ) ?? {};
    const columns = getters.tables?.buildings.columns ?? [];
    const valueOf = (column) =>
      Object.entries(building).find(([name]) => name === column.name)?.[1] ??
      column.default_value;
    const result = columns
      ?.filter((c) => buildingEntryNames.includes(c.name))
      .sort((a, b) => a.order - b.order)
      .map((column) =>
        Object.assign(column, { key: createUUID(), value: valueOf(column) })
      );

    return result ?? old;
  }),
  selectedProjectMetadata: (s) => s.selectingProject?.result?.metadata,
  selectedProject: (s) => s.selectingProject?.result?.project,
  projects: memoized(
    (s) => (old) => s.loadingProjectsMetadata?.result?.records ?? old ?? []
  ),
  clients: memoized(
    (s) => (old) => s.loadingClients?.result?.records ?? old ?? []
  ),
  /** @deprecated layout step is no longer used */
  buildings: memoized(
    (state) => (old) =>
      state?.loadingBuildings?.result?.buildings?.records ?? old ?? []
  ),
  risks: memoized(
    (s) => (old) =>
      s.loadingRisks?.result?.records?.map((risk) => ({
        ...risk,
        risk: risk.chance * risk.consequence,
      })) ??
      old ??
      []
  ),
  roles: memoized(
    (s) => (old) => state.loadingUsersAndRoles.result?.roles ?? old ?? []
  ),
  members: memoized(
    (s) => (old) => state.loadingUsersAndRoles.result?.members ?? old ?? []
  ),
  users: memoized(
    (s) => (old) => state.loadingUsersAndRoles.result?.users ?? old ?? []
  ),
  firm: (s) => s.selectingProject?.result?.metadata?.firm,
};

const projectSetupRunners = {
  async project(context) {
    const { name, date } = context.state.projectSetup;
    // async: the template runs on the queue. Applied inside this request, a
    // large one outlasted the proxy's time limit.
    const project = await createProjectOS({
      license: context.rootGetters.project.license,
      name,
      description: `Project ${name} created on ${date} `,
      number: `C#${generateUniqueCode()}`,
      template_mode: 'async',
      modules: defaultModules,
      roles: rolesToModulesMapping,
    });
    context.commit('updateProjectSetup', { project });
  },
  // The template is what creates the project's OS roles. Assigning users
  // before it finishes only reaches V1, so wait for it however long the
  // queue takes.
  async template(context, progress, previous) {
    const projectId = context.state.projectSetup.project.id;

    // A failed template stays failed; retrying it means queueing it again.
    if (previous === 'failed') {
      await retryProjectSetupOS(projectId);
    }

    for (;;) {
      const { template } = await fetchProjectSetupOS(projectId);

      if (!template) {
        return { detail: 'Geen template voor dit projecttype' };
      }
      if (template.status === 'completed') {
        return {};
      }
      if (template.status === 'failed') {
        throw new Error(
          `Template mislukt: ${template.error ?? 'onbekende fout'}`
        );
      }

      progress(template.status === 'pending' ? 'In de wachtrij…' : 'Bezig…');
      await sleep(TEMPLATE_POLL_MS);
    }
  },
  // The OS V2 API no longer creates the V1 structures (admin role, modules,
  // predefined roles), so provision them through the launcher API. No-op
  // server-side when the project already has them.
  async v1(context) {
    await provisionProjectV1(context.state.projectSetup.project.id, {
      modules: defaultModules,
      roles: rolesToModulesMapping,
    });
  },
  async record(context) {
    const { name, date, project } = context.state.projectSetup;
    const record = await createRecord({
      project: { id: context.rootGetters.project.id },
      table: { id: context.getters.tables.metadata },
      record: {
        name,
        project_id: project.id,
        project_number: project.number,
        is_concept: true,
        created: date,
      },
    });
    context.state.loadingProjectsMetadata.result?.records?.push(record);
    context.commit('updateProjectSetup', { record });
  },
  // The roles screen pairs each V1 role with the OS role of the same name.
  // Informs only: a role the template does not create is not in ANT OS yet,
  // and its users are kept in the launcher until it is.
  async roles(context) {
    let osRoles;
    try {
      const response = await fetchProjectRolesOS(
        context.state.projectSetup.project.id
      );
      osRoles = Array.isArray(response) ? response : (response?.data ?? []);
    } catch (error) {
      console.error(error);
      return { detail: 'Rollen in ANT OS konden niet worden gecontroleerd' };
    }

    const missing = missingOsRoles(osRoles);
    context.commit('updateProjectSetup', { missingOsRoles: missing });

    return {
      detail:
        missing.length > 0
          ? `Nog niet in ANT OS (geen probleem): ${missing.join(', ')}`
          : `${rolesToModulesMapping.length} rollen aanwezig`,
    };
  },
};

function missingOsRoles(osRoles) {
  return rolesToModulesMapping
    .map(({ role }) => role)
    .filter((role) => !osRoles.some((osRole) => osRole.name === role));
}

function generateUniqueCode() {
  return Math.random().toString(36).substring(2, 7);
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
